/**
 * MONSTER TOWN — COOKING CREW WIRING (src/cooking/**).
 *
 * The one `installCooking(ctx)` line this crew owns in `src/main.ts`, and every
 * wire that makes the kitchen part of the world rather than a panel that floats
 * over it. All of it is the event bus; this crew imports no other crew's module
 * except the shared UI primitives (`dom`, `icons`, `panels`), which are pure
 * functions.
 *
 * FOUR WIRES:
 *
 *  1. `spawn:request` / `cook:pick:<itemId>` — a garden or pantry placeable was
 *     tapped, so that ingredient goes in the bag. The venue crew's stations use
 *     `venue:add:<itemId>` in exactly this shape; nothing new was invented.
 *  2. `spawn:request` / `dish` — the interiors crew's COOKER already spawns a
 *     plate of food on every third tap. That is now also the way into the
 *     kitchen, so the stove a kid already knows opens real cooking, and
 *     `src/content/furniture.ts` (theirs) is untouched.
 *  3. `kitchen:cooked` -> `errand:observe {kind:'make'}` — this is how the
 *     reference errand's third step gets ticked. The errand engine adapts
 *     build/gather/catch for free but a `make` needs a real emitter, and this is
 *     it (contract §12: "talk, make and deliver need a real emitter").
 *  4. seeding — a brand new town gets a fruit tree, a veg patch, a berry bush,
 *     a cooking pot and a pantry, so the whole errand is walkable on day one
 *     with zero coins and nothing bought.
 *
 * NO NEW SAVE SECTION. Seeding is idempotent by INSPECTION (is one already in
 * this scene?), the same way the economy crew seeds the job board — so this
 * expansion adds no schema surface at all on top of the architect's seven, and
 * `npm run save-migration` has nothing new to survive.
 */

import { bus } from '../core/events'
import { inventory } from '../core/inventory'
import { save } from '../core/save'
import { world } from '../core/scene'
import type { Cell, Rot } from '../core/grid'
import type { PlaceableId, SceneId } from '../core/types'
import type { GameContext } from '../app/context'
import { place } from '../build/placer'
import { registerAngle, registerSpot } from '../harness'
import {
  GARDEN_SEED,
  berryBush,
  cookPot,
  fruitTree,
  kitchenShelf,
  kitchenWindow,
  pantry,
  vegPatch,
  INGREDIENTS,
  RECIPES,
} from '../content/recipes'
import { allRecipes, registerIngredients, registerRecipes } from './types'
import { cookSheetState, installCookSheet, openCookSheet } from './sheet'

/**
 * `window.__cook` — this crew's own QA handle. Deliberately NOT part of
 * `HarnessApi` (that interface is frozen); additive and read-only apart from
 * `open()`, which is the same call the cooking pot makes.
 */
export interface CookDebugApi {
  state(): ReturnType<typeof cookSheetState>
  recipes(): string[]
  ingredients(): string[]
  open(recipeId?: string): void
}

declare global {
  interface Window {
    __cook?: CookDebugApi
  }
}

const TOWN = 'town' as SceneId
const KITCHEN = 'house_kitchen' as SceneId

const PICK_PREFIX = 'cook:pick:'

function hasDef(sceneId: SceneId, defId: PlaceableId): boolean {
  const node = world.get(sceneId)
  if (node === undefined) return false
  return node.grid.all().some((p) => p.defId === defId)
}

function seedOne(sceneId: SceneId, defId: PlaceableId, anchor: Cell, rot: Rot = 0): void {
  if (hasDef(sceneId, defId)) return
  place(sceneId, defId, anchor, rot, { silent: true })
}

/**
 * A KID CAN ALWAYS GET FOOD. This is the reason the reference errand is
 * playable on a brand new town: three fruit are four taps away, in the kid's
 * own garden, inside their own fence.
 */
function seedGardenAndKitchen(): void {
  seedOne(TOWN, fruitTree.id, GARDEN_SEED.fruitTree)
  seedOne(TOWN, vegPatch.id, GARDEN_SEED.vegPatch)
  seedOne(TOWN, berryBush.id, GARDEN_SEED.berryBush)
  seedOne(KITCHEN, cookPot.id, GARDEN_SEED.cookPot)
  seedOne(KITCHEN, pantry.id, GARDEN_SEED.pantry)
  // THE ROOM LOOKS LIKE THE PICTURE OF IT. The cook sheet paints a window with
  // hills and a sun and a shelf of jars with a pan over it; a kid tapping the
  // pot used to leave a bare mint box to arrive in one. Idempotent by inspection
  // like everything else here, so an existing town gains them on next load and
  // no save section is added (contract §7: this expansion adds no schema).
  // rot 3 = facing east, backs against the WEST wall. See the note on
  // `GARDEN_SEED.window` for why they are not over the counter run.
  seedOne(KITCHEN, kitchenWindow.id, GARDEN_SEED.window, 3)
  seedOne(KITCHEN, kitchenShelf.id, GARDEN_SEED.shelf, 3)
}

export function installCooking(ctx: GameContext): void {
  // Content first: the sheet reads these the moment it is opened.
  registerIngredients(INGREDIENTS)
  registerRecipes(RECIPES)
  installCookSheet(ctx.hud)

  window.__cook = {
    state: () => cookSheetState(),
    recipes: () => allRecipes().map((r) => r.id),
    ingredients: () => INGREDIENTS.map((i) => i.id),
    open: (recipeId) => openCookSheet(recipeId),
  }

  // 1 + 2. the tap verbs
  bus.on('spawn:request', (e) => {
    if (e.what === 'dish') {
      // The cooker made something. Open the real kitchen behind it.
      openCookSheet()
      return
    }
    if (!e.what.startsWith(PICK_PREFIX)) return
    const itemId = e.what.slice(PICK_PREFIX.length)
    if (itemId === '') return
    // `inventory.add` emits `inventory:gained`, which the errand engine adapts
    // into a `gather` observation for free — so picking fruit ticks the errand
    // with no code here knowing an errand exists.
    inventory.add(itemId, 1, 'garden')
    bus.emit('fx:play', { effect: 'sparkle', at: { ...e.at }, count: 5 })
    save.request('cooking:picked')
  })

  // 3. cooking is a `make` — the wire the errand engine cannot adapt for itself
  bus.on('kitchen:cooked', (e) => {
    bus.emit('errand:observe', { kind: 'make', what: e.recipeId, count: e.count })
  })

  // 4. seeding, on every load, cheap and idempotent
  bus.on('save:loaded', () => seedGardenAndKitchen())

  /**
   * Harness views, once the world is up. A tool has to be able to stand a
   * monster NEXT TO the fruit tree — a tap at a world point that is off the
   * edge of the glass is not a tap, which is exactly the class of bug the
   * playthrough sweep was rewritten to stop reporting as a dead prop.
   */
  bus.on('app:booted', () => {
    const node = world.get(TOWN)
    if (node === undefined) return
    const stand = node.grid.cellToWorld({
      gx: GARDEN_SEED.fruitTree.gx,
      gy: 0,
      gz: GARDEN_SEED.fruitTree.gz + 1,
    })
    registerSpot('garden', TOWN, stand)
    registerAngle({ name: 'garden', sceneId: TOWN, target: stand, rot: 0, zoom: 0 })
    const kitchen = world.get(KITCHEN)
    if (kitchen !== undefined) {
      registerSpot(
        'kitchen-pot',
        KITCHEN,
        kitchen.grid.cellToWorld({ gx: GARDEN_SEED.cookPot.gx, gy: 0, gz: GARDEN_SEED.cookPot.gz + 2 }),
      )
    }
  })
  // A room that is registered later than the load (the house rooms are built
  // lazily on first entry) still gets its pot.
  bus.on('scene:entered', (e) => {
    if (e.scene === KITCHEN) seedGardenAndKitchen()
  })
}
